import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import SharesNavbarMenu from "../../../Components/SharesNavbarMenu";
import ListIcon from "@material-ui/icons/List";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import BarChartIcon from "@material-ui/icons/BarChart";
import { Chart } from "react-google-charts";
import "./Dashboard.css";
import { Link, useHistory } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { AuthClientApi } from "../../../utils/api";
import { toast } from "react-toastify";

const customStyles = {
  dashboardContainer: {
    backgroundColor: "#ffffff",
    borderColor: "#8d8c8c",
  },
  gradientBackground: {
    background: "linear-gradient(135deg, #e8e8e8, #d0d0d0)",
  },
};

const chartData = [
  ["Month", "Plans", "Transactions", "Report"],
  ["Jan", 12, 24, 40],
  ["Feb", 18, 30, 55],
  ["Mar", 22, 45, 80],
  ["Apr", 78, 60, 90],
  ["May", 32, 70, 17],
];

const chartOptions = {
  title: "Monthly Overview",
  chartArea: { width: "60%" },
  hAxis: {
    title: "Total Count",
    minValue: 0,
  },
  vAxis: {
    title: "Month",
  },
  colors: ["#3b5998", "#00b894", "#fdcb6e"],
};

export default function Dashboard() {
  const history = useHistory();

  // API States
  const [countsData, setCountsData] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsError, setCountsError] = useState(null);

  // Recent Activity API States
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  const dashboardData = [
    {
      title: "Plans",
      count: "Plans",
      link: "/shares-platform/plans",
      icon: <ListIcon style={{ fontSize: 40, color: "#fff" }} />,
      bgColor: "#00b894",
      statLabel: "Pending Plans",
      statValue: countsData?.pendingPlans ?? 0,
    },
    {
      title: "Funds",
      count: "Funds",
      link: "/shares-platform/fund",
      icon: <AttachMoneyIcon style={{ fontSize: 40, color: "#fff" }} />,
      bgColor: "#0984e3",
      statLabel: "Pending Funds",
      statValue: countsData?.pendingFunds ?? 0,
    },
    {
      title: "Transactions",
      count: "Transactions",
      link: "/shares-platform/transaction",
      icon: <CompareArrowsIcon style={{ fontSize: 40, color: "#fff" }} />,
      bgColor: "#6c5ce7",
      statLabel: "Transactions",
      statValue: countsData?.transactions ?? 0,
    },
    {
      title: "Report",
      count: "Report",
      link: "/shares-platform/report",
      icon: <BarChartIcon style={{ fontSize: 40, color: "#fff" }} />,
      bgColor: "#fdcb6e",
      statLabel: "Transaction Reports",
      statValue: countsData?.transactionReports ?? 0,
    },
  ];


  // Fetch Blockshares Count
  const fetchBlocksharesCount = async () => {
    setCountsLoading(true);
    setCountsError(null);

    try {
      const response = await AuthClientApi.get(
        "/client/blockshares/fetch-blockshares-count"
      );
      const data = response.data;

      // Validate response structure
      if (data.status && data.counts) {
        setCountsData(data.counts);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      console.error("API Error:", err);
      setCountsError(err.message);
      toast.error(err.message || "Failed to load dashboard counts");
    } finally {
      setCountsLoading(false);
    }
  };

  // Fetch User Activity Logs
  const fetchUserActivityLogs = async () => {
    setActivitiesLoading(true);
    setActivitiesError(null);

    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/user-activity-logs?page=1&limit=10`
      );
      const data = response.data;

      console.log("User Activity Logs Response:", data);

      // Validate response structure
      if (data.success && data.data && data.data.activities) {
        setRecentActivities(data.data.activities);
      } else {
        throw new Error("Invalid API response structure");
      }
    } catch (err) {
      console.error("Activity Logs API Error:", err);
      setActivitiesError(err.message);
      toast.error(err.message || "Failed to load recent activities");
    } finally {
      setActivitiesLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchBlocksharesCount();
    fetchUserActivityLogs();
  }, []);

  const handleBackToDashboard = () => {
    history.push("/dashboard/select");
  };

  return (
    <>
      <SharesNavbarMenu />
      <div
        className="dashboard-wrapper"
        style={customStyles.gradientBackground}
      >
        <Container
          fluid
          className="dashboard-container"
          style={customStyles.dashboardContainer}
        >
          <h1 className="dashboard-title text-center fade-in">
            <button
              onClick={handleBackToDashboard}
              className="back-to-dashboard-btn-dash"
              title="Back to Dashboard"
            >
              <ArrowBackIcon />
            </button>
            {/* Dashboard */}
          </h1>

          {/* Loading State */}
          {countsLoading && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {countsError && (
            <div className="alert alert-danger" role="alert">
              Error loading dashboard counts: {countsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => fetchBlocksharesCount()}
              >
                Retry
              </button>
            </div>
          )}

          {/* Dashboard Cards */}
          {!countsLoading && !countsError && (
            <Row className="dash-card-row fade-in">
              {dashboardData.map((item, index) => (
                <Col xs={12} md={6} lg={3} key={index} className="mb-4">
                  <Link to={item.link} className="text-decoration-none">
                    <Card className="dash-card hover-lift">
                      <div
                        className="card-header-custom"
                        style={{ backgroundColor: item.bgColor }}
                      >
                        <div className="icon">{item.icon}</div>
                        <h5 className="dash-card-title">{item.title}</h5>
                      </div>
                      <Card.Body className="dash-card-body">
                        {/* <div className="stat-label">{item.statLabel}</div> */}
                        <div className="stat-value">{item.statValue}</div>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))}
            </Row>
          )}

          {/* <h3
            className="stats-section-title fade-in text-center mb-3"
            style={{ color: "#ff9800", fontWeight: "bold" }}
          >
            Platform Statistics
          </h3>
          <Row className="stats-cards-row fade-in">
            {stats.map((stat, idx) => (
              <Col xs={12} sm={6} md={3} key={idx} className="mb-4">
                <Card className="stat-card">
                  <Card.Body>
                    <Card.Title className="stat-label">{stat.label}</Card.Title>
                    <Card.Text className="stat-value">{stat.value}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row> */}

          {/* <div className="chart-section fade-in">
            <h3 className="chart-title">Overview</h3>
            <div className="chart-wrapper">
              <Chart
                chartType="BarChart"
                width="100%"
                height="300px"
                data={chartData}
                options={chartOptions}
              />
            </div>
          </div> */}

          <div className="recent-activity fade-in">
            <h3>Recent Activity</h3>

            {/* Loading State */}
            {activitiesLoading && recentActivities.length === 0 && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "100px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {activitiesError && (
              <div className="alert alert-danger" role="alert">
                Error loading activities: {activitiesError}
                <button
                  className="btn btn-outline-danger btn-sm ms-2"
                  onClick={() => fetchUserActivityLogs()}
                >
                  Retry
                </button>
              </div>
            )}

            {/* Activities List */}
            {!activitiesError && recentActivities.length > 0 && (
              <div className="activity-list">
                {recentActivities.map((act) => (
                  <div key={act.id} className="activity-item">
                    <div className="activity-date">
                      {new Date(act.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                    <div className="activity-text">{act.description}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!activitiesLoading && !activitiesError && recentActivities.length === 0 && (
              <div className="text-center text-muted py-4">
                No recent activities found
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  );
}
